import styles from './TariffDetailsContainer.module.css'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { IFButton, IFText } from 'Components'
import { Colors } from 'Theme'
import moment from 'moment'
import AuthSelectors from 'Stores/Auth/Selectors'
import PropTypes from 'prop-types'
import TariffModal from 'Containers/TariffModal/TariffModal'
import { useEffect, useRef } from 'react'
import TariffActions from 'Stores/Tariff/Actions'
import TariffSelectors from 'Stores/Tariff/Selectors'
import RequestState from 'Enums/RequestState'
import InfinityEnums from 'Enums/InfinityEnums'

const TariffDetailsContainer = ({
  row,
  operatorUser,
  updateTariff,
  updateTariffRequestState,
  deleteTariff,
  deleteTariffRequestState,
}) => {
  const { t } = useTranslation()
  const tariffModalRef = useRef()
  useEffect(() => {
    if (
      updateTariffRequestState === RequestState.SUCCEEDED ||
      deleteTariffRequestState === RequestState.SUCCEEDED
    ) {
      tariffModalRef.current.dismiss()
    }
  }, [updateTariffRequestState])
  const canEdit = operatorUser.permissions.includes(
    InfinityEnums.OperatorUserPermission.CAN_EDIT_TARIFF,
  )

  const elements = row.original.elements
  if (!elements || !Array.isArray(elements) || elements.length === 0) {
    return null
  }
  const TariffDetailsContainerItems = [
    {
      key: 'elements',
      value: elements,
      translationKey: 'TariffList.Elements',
    },
  ]
  const getChangedTariffFields = (updatedTariff, oldTariff) => {
    if (!updatedTariff) return {}

    const changes = {}

    const cleanObject = (obj) => {
      if (!obj) return obj
      if (Array.isArray(obj)) {
        return obj.map((item) => cleanObject(item))
      }
      if (typeof obj === 'object' && !(obj instanceof Date)) {
        const cleaned = {}
        for (const key in obj) {
          if (key === '_id') {
            continue
          }
          cleaned[key] = cleanObject(obj[key])
        }
        return cleaned
      }

      return obj
    }
    const normalizeForComparison = (val) => {
      if (val instanceof Date) return val.getTime()
      if (typeof val === 'string' && !isNaN(Date.parse(val))) {
        return new Date(val).getTime()
      }
      return val
    }

    const isEqual = (val1, val2) => {
      const clean1 = cleanObject(val1)
      const clean2 = cleanObject(val2)
      if (clean1 === clean2) return true
      if (clean1 == null || clean2 == null) return false
      const isDate1 =
        clean1 instanceof Date ||
        (typeof clean1 === 'string' && !isNaN(Date.parse(clean1)))
      const isDate2 =
        clean2 instanceof Date ||
        (typeof clean2 === 'string' && !isNaN(Date.parse(clean2)))

      if (isDate1 && isDate2) {
        const time1 = normalizeForComparison(clean1)
        const time2 = normalizeForComparison(clean2)
        return time1 === time2
      }

      if (Array.isArray(clean1) && Array.isArray(clean2)) {
        if (clean1.length !== clean2.length) return false
        return clean1.every((item, index) => isEqual(item, clean2[index]))
      }

      if (typeof clean1 === 'object' && typeof clean2 === 'object') {
        const keys1 = Object.keys(clean1)
        const keys2 = Object.keys(clean2)
        if (keys1.length !== keys2.length) return false
        return keys1.every((key) => isEqual(clean1[key], clean2[key]))
      }

      return clean1 === clean2
    }

    const wasRemoved = (newVal, oldVal) => newVal == null && oldVal != null

    const setChange = (field, newVal, oldVal) => {
      if (wasRemoved(newVal, oldVal)) {
        changes[field] = null
      } else if (!isEqual(newVal, oldVal)) {
        changes[field] = newVal
      }
    }

    const simpleFields = ['name', 'type', 'startDate', 'expiryDate']
    simpleFields.forEach((field) => {
      setChange(field, updatedTariff[field], oldTariff?.[field])
    })

    setChange('minPrice', updatedTariff.minPrice, oldTariff?.minPrice)
    setChange('maxPrice', updatedTariff.maxPrice, oldTariff?.maxPrice)

    if ('elements' in updatedTariff) {
      setChange('elements', updatedTariff.elements, oldTariff?.elements)
    }

    return changes
  }
  const handleTariffSubmit = (tariff) => {
    const changes = getChangedTariffFields(tariff, row.original)
    if (Object.keys(changes).length > 0) {
      updateTariff(row.original._id, { tariff: changes })
    }
  }
  const handleDelete = () => {
    deleteTariff(row.original._id)
  }

  return (
    <div className={styles.MainDiv}>
      <div
        className={styles.TariffDetailsContainerItems}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.Line}>
          <div
            style={{ borderLeft: `1px solid ${Colors.DividerColor}` }}
            className={styles.Divider}
          />
          <div
            style={{ background: Colors.DividerColor }}
            className={styles.Circle}
          />
        </div>
        <div className={styles.Details}>
          <div className={styles.Titles}>
            <IFText
              className={styles.TitleTextPrice}
              style={{ color: Colors.text }}
            >
              {t('TariffList.PriceComponent')}
            </IFText>
            <IFText
              className={styles.TitleTextRest}
              style={{ color: Colors.text }}
            >
              {t('TariffList.Restrictions')}
            </IFText>
          </div>

          {TariffDetailsContainerItems[0]?.value.map((block, idx) => {
            const items = TariffDetailsContainerItems[0]?.value

            const restrictions = block.restrictions ? block.restrictions : null
            let display
            if (restrictions && restrictions.dayOfWeek) {
              const days = restrictions?.dayOfWeek ?? []
              const toTitle = (s) =>
                s?.toString().charAt(0).toUpperCase() +
                s?.toString().slice(1).toLowerCase()
              const firstTwo = days.slice(0, 2).map(toTitle)
              const remaining = Math.max(days.length - firstTwo.length, 0)
              display =
                remaining > 0
                  ? `${firstTwo.join(', ')} +${remaining}`
                  : firstTwo.join(', ')
            }

            return (
              <>
                <div
                  className={styles.TariffCard}
                  key={block?.restrictions?.startTime ?? idx}
                >
                  <div className={styles.priceComponents}>
                    {block.priceComponents?.map((pc, pcIdx) => {
                      const getUnit = (type) => {
                        switch (type?.toUpperCase()) {
                          case InfinityEnums.TariffDimensionType.ENERGY:
                            return t('TariffList.kWh')
                          case InfinityEnums.TariffDimensionType.TIME:
                            return t('TariffList.Min')
                          case InfinityEnums.TariffDimensionType.PARKING_TIME:
                            return t('TariffList.Min')
                          case InfinityEnums.TariffDimensionType.FLAT:
                            return ''
                          default:
                            return ''
                        }
                      }

                      const unit = getUnit(pc.type)

                      const isParkingTime =
                        pc.type?.toUpperCase() ===
                        InfinityEnums.TariffDimensionType.PARKING_TIME
                      const displayPrice =
                        isParkingTime && pc.price != null
                          ? Number((Number(pc.price) / 60).toFixed(4))
                          : pc.price

                      return (
                        <div
                          key={pcIdx}
                          style={{
                            marginBottom:
                              pcIdx < block.priceComponents.length - 1
                                ? '16px'
                                : '0',
                          }}
                        >
                          <div className={styles.HeaderRow}>
                            <IFText className={styles.HeaderLabel}>
                              {pc.type} {t('TariffList.Dash')} {displayPrice}{' '}
                              {operatorUser?.operator.country.currency.code}
                              {unit && `/${unit}`}
                            </IFText>
                          </div>
                          <div className={styles.DetailsGrid}>
                            <div className={styles.LabelContainer}>
                              <div className={styles.LabelTitle}>
                                <IFText
                                  className={styles.DetailText}
                                  style={{ color: Colors.DetailPanel }}
                                >
                                  {t('TariffList.Step')}
                                </IFText>
                              </div>
                              <div className={styles.LabelValue}>
                                <IFText className={styles.DetailText}>
                                  {pc.stepSize} {unit}
                                </IFText>
                              </div>
                            </div>
                            <div className={styles.LabelContainer}>
                              <div className={styles.LabelTitle}>
                                <IFText
                                  className={styles.DetailText}
                                  style={{ color: Colors.DetailPanel }}
                                >
                                  {t('TariffList.Vat')}
                                </IFText>
                              </div>
                              <div className={styles.LabelValue}>
                                <IFText className={styles.DetailText}>
                                  {pc.vat != null
                                    ? `${pc.vat} ${t('TariffList.Percentage')}`
                                    : t('TariffList.NotSet')}
                                </IFText>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className={styles.Restrictions}>
                    {restrictions == null ? (
                      <IFText>{t('TariffList.NoRestrictions')}</IFText>
                    ) : (
                      <div className={styles.RestrictionsGrid}>
                        {(restrictions.minPower || restrictions.maxPower) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.minPower && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MinPower')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.minPower}{' '}
                                    {t('TariffList.PowerUnit')}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.maxPower && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MaxPower')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.maxPower}{' '}
                                    {t('TariffList.PowerUnit')}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions.minDuration ||
                          restrictions.maxDuration) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.minDuration && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MinDuration')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.minDuration}{' '}
                                    {t('TariffList.Min')}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.maxDuration && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MaxDuration')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.maxDuration}{' '}
                                    {t('TariffList.Min')}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions.minParkingDuration ||
                          restrictions.maxParkingDuration) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.minParkingDuration && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MinParkingDuration')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.minParkingDuration}{' '}
                                    {t('TariffList.Sec')}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.maxParkingDuration && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MaxParkingDuration')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.maxParkingDuration}{' '}
                                    {t('TariffList.Sec')}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions.minKwh || restrictions.maxKwh) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.minKwh && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MinEnergy')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.minKwh}{' '}
                                    {t('TariffList.EnergyUnit')}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.maxKwh && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MaxEnergy')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.maxKwh}{' '}
                                    {t('TariffList.EnergyUnit')}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions.minCurrent ||
                          restrictions.maxCurrent) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.minCurrent && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MinCurrent')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.minCurrent}{' '}
                                    {t('TariffList.CurrentUnit')}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.maxCurrent && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.MaxCurrent')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {restrictions.maxCurrent}{' '}
                                    {t('TariffList.CurrentUnit')}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions.startTime || restrictions.endTime) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.startTime && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.StartTime')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {moment(restrictions.startTime).format(
                                      'hh:mm A',
                                    )}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.endTime && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.EndTime')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {moment(restrictions.endTime).format(
                                      'hh:mm A',
                                    )}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions.startDate || restrictions.endDate) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions.startDate && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.StartDate')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {moment(restrictions.startDate).format(
                                      'MMM DD, YYYY',
                                    )}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions.endDate && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.EndDate')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {moment(restrictions.endDate).format(
                                      'MMM DD, YYYY',
                                    )}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(restrictions?.dayOfWeek ||
                          restrictions?.reservation) && (
                          <div className={styles.RestrictionItem}>
                            {restrictions?.dayOfWeek && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.DaysOfWeek')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText
                                    className={styles.DetailTextDaysOfWeek}
                                  >
                                    {display}
                                  </IFText>
                                </div>
                              </div>
                            )}

                            {restrictions?.reservation && (
                              <div className={styles.LabelContainer}>
                                <div className={styles.LabelTitle}>
                                  <IFText
                                    className={styles.DetailText}
                                    style={{ color: Colors.DetailPanel }}
                                  >
                                    {t('TariffList.Reservation')}
                                  </IFText>
                                </div>
                                <div className={styles.LabelValue}>
                                  <IFText className={styles.DetailText}>
                                    {typeof restrictions.reservation ===
                                    'object'
                                      ? restrictions.reservation.required
                                        ? t('TariffList.ReservationRequired')
                                        : t('TariffList.ReservationAllowed')
                                      : String(restrictions.reservation)}
                                  </IFText>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {items.length > 1 && idx < items.length - 1 && (
                  <div
                    className={styles.DividerItems}
                    style={{ borderTop: `1px solid ${Colors.DividerColor}` }}
                  />
                )}
              </>
            )
          })}
        </div>
      </div>
      {canEdit && (
        <IFButton
          color={Colors.primary}
          isFill={false}
          text={'Edit'}
          size="xsm"
          className={styles.EditButton}
          onClick={(e) => {
            e.stopPropagation()
            tariffModalRef.current.show()
          }}
        />
      )}
      <TariffModal
        ref={tariffModalRef}
        row={row.original}
        onSubmit={handleTariffSubmit}
        isEditing={true}
        updateTariffRequestState={updateTariffRequestState}
        deleteTariffRequestState={deleteTariffRequestState}
        handleDelete={handleDelete}
        operator={operatorUser.operator}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    updateTariff: (tariffID, tariff) =>
      dispatch(TariffActions.updateTariff(tariffID, tariff)),
    deleteTariff: (tariffID) => dispatch(TariffActions.deleteTariff(tariffID)),
  }
}

const mapStateToProps = (state) => ({
  operatorUser: AuthSelectors.getOperatorUser(state),
  updateTariffRequestState: TariffSelectors.getUpdateTariffRequestState(state),
  deleteTariffRequestState: TariffSelectors.getDeleteTariffRequestState(state),
})

TariffDetailsContainer.propTypes = {
  row: PropTypes.object,
  operatorUser: PropTypes.object,
  updateTariff: PropTypes.func,
  updateTariffRequestState: PropTypes.number,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TariffDetailsContainer)
